import { getRoot } from "@gadgetinc/mobx-quick-tree";
import type { Dominion } from "../../database/Dominion";
import { GadgetSystemDominionId } from "../../database/DominionLiterals";
import type { ModelField } from "../../database/ModelField";
import { GadgetSessionShopifySIDKey, GadgetSessionToCustomerKey, GadgetSessionToShopKey } from "./keys";

interface ShopifyDominion extends Dominion {
  id: "shopify/dominion/shopify";
}

// Field keys for Shopify dominion fields that can only be deleted when there is no active Shopify connection
export const conditionallyRemovableShopifyDominionFieldKeys = [
  GadgetSessionShopifySIDKey,
  GadgetSessionToShopKey,
  GadgetSessionToCustomerKey,
];

export const ShopifyDominion: ShopifyDominion = {
  id: "shopify/dominion/shopify" as const,
  name: "Shopify",
  sentenceName: "Shopify",
  description: "Data where Shopify is the source of truth",
  supportsMetadata() {
    return false;
  },
  supportsFieldValidations() {
    return false;
  },
  supportsFieldRemoval(field: ModelField) {
    if (field.shopifyFieldKey === "id" || field.shopifyGraphQLFieldKey === "id") {
      return false;
    } else if (conditionallyRemovableShopifyDominionFieldKeys.includes(field.key)) {
      const root = getRoot(field);
      const activeShopifyConnection = root.maybeGetShopifyConnection();
      if (field.key === GadgetSessionToCustomerKey) {
        return !activeShopifyConnection?.configuration?.customerAuthenticationEnabled;
      }
      return !activeShopifyConnection;
    } else {
      return true;
    }
  },
  supportsFieldDuplication() {
    return false;
  },
  supportsFieldTypeChanges() {
    return false;
  },
  supportsFieldRename() {
    return false;
  },
  supportsFieldFeature() {
    return false;
  },
  displaysFieldDataOffsite() {
    return true;
  },
  fieldIsWritablePublicly(field: ModelField) {
    return field.contributorConfiguration[GadgetSystemDominionId]?.isWritablePublicly ?? true;
  },
  fieldIsWritableInternally(field: ModelField) {
    return field.contributorConfiguration[GadgetSystemDominionId]?.isWritableInternally ?? true;
  },
};
